// SAM-1095 / SAM-1096 — full-page ad editor (/settings/ads/new, /settings/ads/:adId), mock C4.
// Replaces AdDetailDrawer. The card preview cuts the text at two lines, exactly as the app does.
import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Info, Trash2 } from 'lucide-react';
import { useAds, useCreateAd, useUpdateAd, useDeleteAd } from '../../hooks/useAds';
import type { Ad } from '../../hooks/useAds';
import { useArticles } from '../../hooks/useArticles';
import { useBusinesses } from '../../hooks/useBusinessAccessType';
import { useUserTypes } from '../../hooks/useUserTypes';
import { useAdThemes } from '../../hooks/useAdThemes';
import { useFdCan } from '../../hooks/useFdCan';
import { useWhiteBorderCheck } from '../../hooks/useWhiteBorderCheck';
import { useT } from '../../i18n/admin';
import { SEMICOLON_MESSAGE } from '../../utils/crudText';
import { S3ImageUpload } from '../../components/S3ImageUpload';
import { DateField } from '../../components/ui/DateTimeField';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { BilingualPair } from '../../components/cms/BilingualPair';
import { CmsStateBadge } from '../../components/cms/CmsStateBadge';
import { FeaturedPreview, PhoneFrame } from '../../components/cms/AppPreviews';
import { Card, Chip, Field, PageHead, Segmented, btnGhost, btnPrimary, primaryStyle, selectClass, inputClass } from '../../components/cms/EditorParts';
import { AD_TEXT_COLOR, DEFAULT_AD_THEME_HEX, MIN_AD_CONTRAST, contrastRatio, isNearWhite } from '../../config/adThemes';
import { cmsState, todayInBusinessClock } from '../../utils/cmsState';
import {
  AD_TEXT_FIT, AD_TITLE_MAX, EMPTY_AD_FORM, addDays, adMissing, adPayload, adToForm, endOfCarousel, type AdForm, type LinkAction,
} from './cmsForms';

export interface AdDraftState { draft?: AdForm }

const createdId = (res: unknown): number | undefined => {
  const d = (res as { data?: unknown } | undefined)?.data;
  const first = Array.isArray(d) ? d[0] : d;
  const id = (first as { id?: number } | undefined)?.id;
  return typeof id === 'number' ? id : undefined;
};

const FIELD_LABEL: Record<string, string> = {
  title_en: 'Title (English)', title_es: 'Title (Español)', content_en: 'Content (English)', content_es: 'Content (Español)',
  short_description_en: 'Short text (English)', short_description_es: 'Short text (Español)',
};

export const AdEditorPage: React.FC = () => {
  const { adId } = useParams<{ adId?: string }>();
  const location = useLocation();
  const isCreate = !adId || adId === 'new';
  const { ads, isLoading } = useAds();
  const restored = (location.state as AdDraftState | null)?.draft;
  const ad = isCreate ? undefined : ads.find((a) => String(a.id) === adId);
  const navigate = useNavigate();

  if (!isCreate && isLoading) return <div className="p-8 text-sm text-gray-500">Loading…</div>;
  if (!isCreate && !ad) {
    return (
      <div className="p-8 text-sm text-gray-600">
        <p className="mt-0">That ad does not exist (or it was deleted).</p>
        <button type="button" className={btnGhost} onClick={() => navigate('/settings/ads')}>Back to Ads</button>
      </div>
    );
  }
  return <AdEditor key={adId ?? 'new'} ad={ad} restored={restored} />;
};

const LINK_OPTIONS: { id: LinkAction; text: string }[] = [
  { id: '', text: 'Nothing' },
  { id: 'article', text: 'An article' },
  { id: 'business', text: 'A business' },
  { id: 'url', text: 'A website' },
];

const AdEditor: React.FC<{ ad?: Ad; restored?: AdForm }> = ({ ad, restored }) => {
  const navigate = useNavigate();
  const { fmt } = useT();
  const { ads } = useAds();
  const { articles } = useArticles();
  const { data: businessesData } = useBusinesses();
  const { userTypes } = useUserTypes();
  const { presets } = useAdThemes();
  const createAd = useCreateAd();
  const updateAd = useUpdateAd();
  const deleteAd = useDeleteAd();
  const canEdit = useFdCan('cms', 'edit');
  const whiteBorder = useWhiteBorderCheck();
  const isCreate = !ad;

  const [form, setForm] = useState<AdForm>(() => restored ?? (ad ? adToForm(ad) : { ...EMPTY_AD_FORM, background_color: DEFAULT_AD_THEME_HEX }));
  const [baseline, setBaseline] = useState<AdForm>(ad ? adToForm(ad) : EMPTY_AD_FORM);
  const [previewLang, setPreviewLang] = useState<'en' | 'es'>('en');
  const [previewView, setPreviewView] = useState<'ad' | 'carousel'>('ad');
  // What the last Save / Publish click tried, so the errors that show are the ones for that action.
  const [attempt, setAttempt] = useState<'draft' | 'published' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const set = <K extends keyof AdForm>(k: K, v: AdForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const today = todayInBusinessClock();
  const state = cmsState(form, today);
  const isDirty = JSON.stringify(form) !== JSON.stringify(baseline);
  const saving = createAd.isPending || updateAd.isPending;

  const bg = form.background_color || DEFAULT_AD_THEME_HEX;
  // The app paints the card text in black over the colour, so a colour that fails WCAG AA against black is blocked.
  const ratio = contrastRatio(bg, AD_TEXT_COLOR);
  const lowContrast = ratio === null || ratio < MIN_AD_CONTRAST;

  // SAM-1105: white and near-white are not offered, and an ad that still has one cannot be published as it is.
  const whiteBg = isNearWhite(bg);
  const colourProblem = (publishing: boolean) =>
    lowContrast ? 'Black text on this colour is unreadable in the app. Pick a lighter one.'
      : whiteBg && publishing ? 'White looks washed out in the app. Pick a colour.'
        : '';
  const problems = (publishing: boolean) => [...adMissing(form, publishing), ...(colourProblem(publishing) ? [{ field: 'background_color', message: colourProblem(publishing) }] : [])];
  const err = (field: string) => (attempt ? problems(attempt === 'published').find((m) => m.field === field)?.message : undefined);
  const semicolonFields = (attempt ? problems(attempt === 'published') : []).filter((m) => m.message === SEMICOLON_MESSAGE).map((m) => FIELD_LABEL[m.field] ?? m.field);

  const businesses = useMemo(() => {
    const list = Array.isArray(businessesData) ? businessesData : (businessesData as { data?: unknown[] } | undefined)?.data || [];
    return [...(list as { id: string; name: string }[])].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [businessesData]);

  const liveCount = ads.filter((a) => cmsState(a, today) === 'published' && a.id !== ad?.id).length;
  const position = form.priority && state === 'published' ? Math.min(form.priority, liveCount + 1) : liveCount + 1;

  const save = async (status: 'draft' | 'published', next: AdForm = form) => {
    const nextBg = next.background_color || DEFAULT_AD_THEME_HEX;
    const blocked = lowContrast || (status === 'published' && isNearWhite(nextBg));
    const p = [...adMissing(next, status === 'published'), ...(blocked ? [{ field: 'background_color', message: '' }] : [])];
    if (p.length) { setAttempt(status); return; }
    // The ads list may not have loaded when the editor opened, so the place is worked out now, from the live ads.
    // A new ad, or one that is not in the app yet, goes to the end of the carousel.
    const wasLive = !isCreate && cmsState(baseline, today) === 'published';
    const priority = wasLive ? next.priority : endOfCarousel(ads.filter((a) => a.id !== ad?.id && cmsState(a, today) === 'published'));
    const data = adPayload({ ...next, priority }, status);
    try {
      if (isCreate) {
        const res = await createAd.mutateAsync(data);
        const id = createdId(res);
        navigate(id ? `/settings/ads/${id}` : '/settings/ads', { replace: true, state: null });
      } else {
        await updateAd.mutateAsync({ id: ad!.id!, data });
        const saved = { ...next, status };
        setForm(saved);
        setBaseline(saved);
      }
    } catch {
      // The mutation hooks already show the error toast.
    }
  };

  const back = () => {
    if (isDirty && !window.confirm('Discard your unsaved changes?')) return;
    navigate('/settings/ads');
  };

  const published = form.status === 'published';
  const text = (lang: 'en' | 'es') => ({
    title: (lang === 'en' ? form.title_en : form.title_es) || (lang === 'en' ? 'Title' : 'Título'),
    text: (lang === 'en' ? form.short_description_en : form.short_description_es) || '',
    color: bg,
    imageKey: form.art_img_url,
  });
  const toggleUserType = (id: number) =>
    set('user_types_ids', form.user_types_ids.includes(id) ? form.user_types_ids.filter((x) => x !== id) : [...form.user_types_ids, id]);

  const liveAds = ads
    .filter((a) => a.id !== ad?.id && cmsState(a, today) === 'published')
    .sort((a, b) => (a.priority || 9e9) - (b.priority || 9e9) || (a.id ?? 0) - (b.id ?? 0))
    .map((a) => ({ title: (previewLang === 'en' ? a.title_en : a.title_es) || a.title_en, text: (previewLang === 'en' ? a.short_description_en : a.short_description_es) || '', color: a.background_color, imageKey: a.art_img_url }));
  const carousel = [...liveAds.slice(0, Math.max(0, position - 1)), text(previewLang), ...liveAds.slice(Math.max(0, position - 1))];

  return (
    <div className="flex h-full flex-col bg-gray-50 text-gray-900">
      <PageHead
        back="Ads"
        onBack={back}
        title={form.title_en || (isCreate ? 'New ad' : 'Ad')}
        badge={<CmsStateBadge state={state} />}
        aside={published && state === 'published' ? undefined : <span className="text-[13px] text-gray-500">Position {position} of {liveCount + 1} when published</span>}
        actions={canEdit && (published ? (
          <>
            <button type="button" className={btnGhost} disabled={saving} onClick={() => void save('draft')}>Move to draft</button>
            <button type="button" className={btnPrimary} style={primaryStyle} disabled={saving || !isDirty} onClick={() => void save('published')}>Save changes</button>
          </>
        ) : (
          <>
            <button type="button" className={btnGhost} disabled={saving} onClick={() => void save('draft')}>Save draft</button>
            <button type="button" className={btnPrimary} style={primaryStyle} disabled={saving} onClick={() => void save('published')}>Publish</button>
          </>
        ))}
      />

      {state === 'expired' && (
        <div className="mx-8 mt-4 flex flex-wrap items-center gap-3 rounded-lg bg-gray-100 px-4 py-3 text-sm text-gray-800" role="status">
          <span><b>Expired on {fmt.date(form.expiration_date)}.</b> It left the carousel that day.</span>
          <span className="flex-1" />
          {canEdit && (
            <>
              <button type="button" className={btnGhost} onClick={() => void save('published', { ...form, expiration_date: addDays(today, 7) })}>Extend 7 days</button>
              <button type="button" className={btnGhost} onClick={() => void save('published', { ...form, expiration_date: addDays(today, 30) })}>Extend 30 days</button>
            </>
          )}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="flex min-h-full items-stretch">
        <fieldset disabled={!canEdit} className="m-0 flex min-w-0 flex-1 flex-col gap-4 border-0 px-8 pb-12 pt-6">
          <Card title="Card text" subtitle="The card shows two lines of each. Longer text is cut, see the preview.">
            <BilingualPair
              fields={[
                { base: 'title', label: 'Title', error: err('title_en'), note: (v) => `${v.length} / ${AD_TITLE_MAX}` },
                { base: 'short_description', label: 'Short text', multiline: true, note: (v) => `${v.length} characters · about ${AD_TEXT_FIT} fit on the card` },
              ]}
              values={form as unknown as Record<string, string>}
              onChange={(k, v) => setForm((f) => ({ ...f, [k]: v }))}
              disabled={!canEdit}
            />
            {err('title_es') && <span className="text-xs text-red-600">{err('title_es')}</span>}
            {semicolonFields.length > 0 && (
              <span role="alert" className="text-xs text-red-600">{SEMICOLON_MESSAGE} In: {semicolonFields.join(', ')}.</span>
            )}
          </Card>

          <Card title="Look">
            <div className="flex flex-wrap items-center gap-5">
              <S3ImageUpload label="" value={form.art_img_url} onChange={(key) => set('art_img_url', key)} onFileChosen={whiteBorder.onFileChosen} previewHeight="96px" objectFit="cover" />
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-gray-500">Card color</span>
                <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Card color">
                  {presets.filter((p) => !p.disabled).map((p) => (
                    <button
                      key={p.hex}
                      type="button"
                      aria-label={p.name}
                      aria-pressed={bg.toLowerCase() === p.hex.toLowerCase()}
                      title={p.name}
                      onClick={() => set('background_color', p.hex)}
                      className="h-[30px] w-[30px] rounded-lg"
                      style={{ background: p.hex, border: bg.toLowerCase() === p.hex.toLowerCase() ? '2px solid var(--tenant-primary, #0B5C4D)' : '1px solid #D1D5DB' }}
                    />
                  ))}
                  <span className="ml-1.5 text-[13px] text-gray-500">{bg.toUpperCase()} · text is always black</span>
                </div>
                {(err('background_color') || lowContrast || whiteBg) && <span className="text-xs text-red-600">{err('background_color') || colourProblem(true)}</span>}
              </div>
            </div>
            {err('art_img_url') && <span className="text-xs text-red-600">{err('art_img_url')}</span>}
            {whiteBorder.warn && (
              <div role="status" className="rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-900">
                This image has a white or almost white border. It looks washed out in the app: crop the border or pick another photo.
              </div>
            )}
          </Card>

          <Card title="Opens">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Tapping the card opens" htmlFor="cms-ad-link-action">
                <select id="cms-ad-link-action" className={selectClass} value={form.link_action} onChange={(e) => setForm((f) => ({ ...f, link_action: e.target.value as LinkAction, link_id: '' }))}>
                  {LINK_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.text}</option>)}
                </select>
              </Field>
              {form.link_action === 'article' && (
                <Field label="Article" htmlFor="cms-ad-link-id" hint="Changing it here does not change the article.">
                  <select id="cms-ad-link-id" className={selectClass} value={form.link_id} onChange={(e) => set('link_id', e.target.value)}>
                    <option value="">Select an article</option>
                    {articles.map((a) => <option key={a.id} value={a.id}>{a.title_en}</option>)}
                  </select>
                </Field>
              )}
              {form.link_action === 'business' && (
                <Field label="Business" htmlFor="cms-ad-link-id">
                  <select id="cms-ad-link-id" className={selectClass} value={form.link_id} onChange={(e) => set('link_id', e.target.value)}>
                    <option value="">Select a business</option>
                    {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </Field>
              )}
              {form.link_action === 'url' && (
                <Field label="Link" htmlFor="cms-ad-link-id">
                  <input id="cms-ad-link-id" className={inputClass} value={form.link_id} placeholder="https://" onChange={(e) => set('link_id', e.target.value)} />
                </Field>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-gray-500">Who sees it</span>
              <div className="flex flex-wrap gap-1.5">
                {userTypes.map((u) => (
                  <Chip key={u.id} on={form.user_types_ids.includes(u.id)} onClick={() => toggleUserType(u.id)} disabled={!canEdit}>{u.name}</Chip>
                ))}
              </div>
            </div>
          </Card>

          <Card title="Publishing">
            <div className="flex items-center gap-3">
              <span className="w-[60px] text-xs font-semibold text-gray-500">Status</span>
              <Segmented label="Status" value={form.status} options={[{ id: 'draft', text: 'Draft' }, { id: 'published', text: 'Published' }]} onChange={(v) => set('status', v as 'draft' | 'published')} disabled={!canEdit} />
              <span className="flex-1" />
              <span className="text-[13px] text-gray-500">{form.status === 'draft' ? 'Not visible to residents' : state === 'scheduled' ? 'Goes live on its Publishes date' : state === 'expired' ? 'Past its Expires date' : 'In the carousel'}</span>
            </div>
            <div className="flex gap-3">
              <Field label="Publishes" error={err('publish_date')}>
                <DateField ariaLabel="Publishes" value={form.publish_date} onChange={(v) => set('publish_date', v)} invalid={!!err('publish_date')} />
              </Field>
              <Field label="Expires" error={err('expiration_date')}>
                <DateField ariaLabel="Expires" value={form.expiration_date} min={form.publish_date || undefined} onChange={(v) => set('expiration_date', v)} invalid={!!err('expiration_date')} />
              </Field>
            </div>
            <div className="flex items-start gap-2 rounded-lg bg-gray-100 px-3 py-2.5 text-[13px] text-gray-700">
              <Info size={16} className="shrink-0 text-gray-500" aria-hidden />
              <span>Order in the carousel is set by dragging in the Ads list. Ads past their Expires date leave the carousel by themselves.</span>
            </div>
          </Card>

          {canEdit && !isCreate && (
            <div>
              <button type="button" className={`${btnGhost} text-red-700`} onClick={() => setConfirmDelete(true)}>
                <Trash2 size={14} aria-hidden /> Delete ad
              </button>
            </div>
          )}
        </fieldset>

        <aside className="flex w-[380px] shrink-0 flex-col items-center gap-3.5 border-l border-gray-200 bg-gray-100 px-4 py-6" aria-label="Preview">
          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Preview · as residents see it</div>
          <div className="flex flex-wrap justify-center gap-2">
            <Segmented label="Language" value={previewLang} options={[{ id: 'en', text: 'English' }, { id: 'es', text: 'Español' }]} onChange={(v) => setPreviewLang(v as 'en' | 'es')} />
            <Segmented label="Show" value={previewView} options={[{ id: 'ad', text: 'This ad' }, { id: 'carousel', text: 'Whole carousel' }]} onChange={(v) => setPreviewView(v as 'ad' | 'carousel')} />
          </div>
          <PhoneFrame height={560} label={`Ad preview, ${previewLang === 'en' ? 'English' : 'Español'}`}>
            {previewView === 'ad' ? <FeaturedPreview ads={[text(previewLang)]} /> : <FeaturedPreview ads={carousel} active={Math.max(0, position - 1)} />}
          </PhoneFrame>
          <div className="max-w-[300px] text-center text-xs text-gray-500">The second line of the text is cut at 2 lines, exactly as in the app.</div>
        </aside>
      </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => deleteAd.mutate(ad!.id!, { onSuccess: () => navigate('/settings/ads') })}
        title="Delete ad"
        message="This is permanent and cannot be undone."
        confirmText="Delete"
        isLoading={deleteAd.isPending}
      />
    </div>
  );
};
